import { useCallback, useMemo, useState } from 'react';

import {
  useApolloClient,
  useLazyQuery,
  useMutation,
} from '@apollo/client/react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { PlusIcon, RefreshCwIcon, Trash2Icon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { PlacePicker } from '@modules/_shared/components/place-picker/PlacePicker';
import type { ReportComponentProps } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import type { CMAReportType } from '@modules/reports/cma-reports/CMAReportEditor/CMAReportEditor';
import MarketDataSettings from '@modules/reports/cma-reports/MarketDataSettings';
import { FooterActionsComponentWithoutForm } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import { useUpdateCmaReport } from '@modules/reports/shared/utils';
import { useLocale } from '@shared/hooks/locale';

const GET_MARKET_PLACES_BY_IDS = gql(/* GraphQL */ `
  query GetMarketPlacesByIds($ids: [uuid!]!) {
    places(where: { id: { _in: $ids } }) {
      id
      label
      type
    }
  }
`);

const RESET_CMA_MARKET_PLACES = gql(/* GraphQL */ `
  mutation ResetCmaMarketComparisonPlaces($reportId: uuid!) {
    reset_cma_market_comparison_places(args: { _report_id: $reportId }) {
      id
      market_comparison_places
    }

    update_cma_reports_by_pk(pk_columns: { id: $reportId }, _set: {}) {
      id
      updated_at
    }
  }
`);

type MarketPlace = {
  id: string;
  label: string;
  type: string;
};

type MarketDataSettingField =
  | 'market_data_show_map'
  | 'market_data_show_chart'
  | 'market_data_map_zoom_level';

const getPlaceLabel = (place: MarketPlace) => {
  return place.label;
};

const sortPlaces = (
  places: MarketPlace[],
  cmaPropertyId?: string,
): MarketPlace[] => {
  return [...places].sort((a, b) => {
    // CMA property always comes first
    if (a.id === cmaPropertyId) {
      return -1;
    }
    if (b.id === cmaPropertyId) {
      return 1;
    }

    return a.label.localeCompare(b.label);
  });
};

const StepMarketData = (props: ReportComponentProps) => {
  const { t } = useLocale();
  const client = useApolloClient();
  const { data } = useReportContext();
  const cmaReport = data as CMAReportType;

  const marketPlaces = useMemo(
    () => (cmaReport.market_comparison_places ?? []) as MarketPlace[],
    [cmaReport.market_comparison_places],
  );

  const sortedPlaces = useMemo(() => {
    return sortPlaces(marketPlaces, cmaReport.property?.locality_place?.id);
  }, [marketPlaces, cmaReport.property?.locality_place]);

  const [isPlacePickerOpen, setIsPlacePickerOpen] = useState(false);
  const [selectedPlaces, setSelectedPlaces] = useState<string[]>([]);
  const [initialPlaces, setInitialPlaces] = useState<string[]>([]);
  const [getPlaces, { loading: getPlacesLoading }] = useLazyQuery(
    GET_MARKET_PLACES_BY_IDS,
    { notifyOnNetworkStatusChange: true },
  );
  const [deletingPlaceId, setDeletingPlaceId] = useState<string | null>(null);
  const [addPlacesLoading, setAddPlacesLoading] = useState(false);
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  const [isReloadingDefaults, setIsReloadingDefaults] = useState(false);
  const [showReloadConfirmDialog, setShowReloadConfirmDialog] = useState(false);

  const [resetCmaMarketPlaces] = useMutation(RESET_CMA_MARKET_PLACES);

  const isAnyOperationInProgress = Boolean(
    deletingPlaceId ||
      addPlacesLoading ||
      isUpdatingSettings ||
      isReloadingDefaults,
  );

  const [updateCmaReport, isLoading] = useUpdateCmaReport(
    cmaReport.id,
    'page-market-data',
  );

  const handleDeletePlace = async (placeId: string) => {
    if (isAnyOperationInProgress) {
      return;
    }

    setDeletingPlaceId(placeId);
    try {
      const updatedPlaces = marketPlaces.filter(
        (place: MarketPlace) => place.id !== placeId,
      );
      await updateCmaReport({
        market_comparison_places: updatedPlaces,
      });
    } catch (err) {
      console.error('Failed to delete market comparison place', {
        placeId,
        err,
      });
    } finally {
      setDeletingPlaceId(null);
    }
  };

  const handleSettingsUpdate = async (
    field: MarketDataSettingField,
    value: boolean | number,
  ) => {
    if (isAnyOperationInProgress) {
      return;
    }

    setIsUpdatingSettings(true);
    try {
      return await updateCmaReport({ [field]: value });
    } catch (err) {
      console.error('Failed to update market data setting', {
        field,
        value,
        err,
      });
      return;
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const handleReloadDefaults = async () => {
    if (isAnyOperationInProgress) {
      return;
    }

    setShowReloadConfirmDialog(false);
    setIsReloadingDefaults(true);
    try {
      const { data } = await resetCmaMarketPlaces({
        variables: { reportId: cmaReport.id },
      });
      const updated = data?.reset_cma_market_comparison_places?.[0];
      if (updated) {
        client.cache.modify({
          id: client.cache.identify({
            __typename: 'cma_reports',
            id: cmaReport.id,
          }),
          fields: {
            market_comparison_places: () => updated.market_comparison_places,
          },
        });
      }
    } catch (err) {
      console.error('Failed to reload default market comparison places', err);
      return;
    } finally {
      setIsReloadingDefaults(false);
    }
  };

  const fetchNewPlacesData = useCallback(
    async (newIds: string[]) => {
      try {
        const { data } = await getPlaces({ variables: { ids: newIds } });
        return (
          data?.places?.map(place => ({
            id: place.id,
            label: place.label ?? place.id,
            type: place.type,
          })) || []
        );
      } catch (err) {
        console.error('Failed to load places by ids', { newIds, err });
        return [];
      }
    },
    [getPlaces],
  );

  const buildUpdatedPlacesList = useCallback(
    (newPlaces: MarketPlace[]) => {
      return selectedPlaces.map(placeId => {
        const existingPlace = marketPlaces.find(p => p.id === placeId);
        if (existingPlace) {
          return existingPlace;
        }

        const newPlace = newPlaces.find(p => p.id === placeId);
        return newPlace || { id: placeId, label: placeId, type: '' };
      });
    },
    [selectedPlaces, marketPlaces],
  );

  const updateCacheAndDatabase = useCallback(
    async (updatedPlaces: MarketPlace[]) => {
      client.cache.modify({
        id: client.cache.identify({
          __typename: 'cma_reports',
          id: cmaReport.id,
        }),
        fields: { market_comparison_places: () => updatedPlaces },
      });
      await updateCmaReport({ market_comparison_places: updatedPlaces });
    },
    [client.cache, cmaReport.id, updateCmaReport],
  );

  const handleAddPlaces = useCallback(async () => {
    if (selectedPlaces.length === 0) {
      setIsPlacePickerOpen(false);
      return;
    }

    if (isAnyOperationInProgress) {
      return;
    }

    setAddPlacesLoading(true);
    try {
      const existingIds = marketPlaces.map(p => p.id);
      const newIds = selectedPlaces.filter(id => !existingIds.includes(id));

      let updatedPlaces: MarketPlace[];

      if (newIds.length > 0) {
        const newPlaces = await fetchNewPlacesData(newIds);
        updatedPlaces = buildUpdatedPlacesList(newPlaces);
      } else {
        const mapped = selectedPlaces
          .map(placeId => marketPlaces.find(p => p.id === placeId))
          .filter((p): p is MarketPlace => Boolean(p));
        if (mapped.length !== selectedPlaces.length) {
          console.warn(
            'Some selected places not found while reordering; skipping missing entries.',
          );
        }
        updatedPlaces = mapped;
      }

      await updateCacheAndDatabase(updatedPlaces);
      setIsPlacePickerOpen(false);
      setSelectedPlaces([]);
    } catch (err) {
      console.error('Failed to add/update market comparison places', err);
    } finally {
      setAddPlacesLoading(false);
    }
  }, [
    selectedPlaces,
    marketPlaces,
    fetchNewPlacesData,
    buildUpdatedPlacesList,
    updateCacheAndDatabase,
    isAnyOperationInProgress,
  ]);

  return (
    <>
      <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'top', gap: 1 }}>
          <MarketDataSettings
            cmaReport={cmaReport}
            onUpdate={handleSettingsUpdate}
          />
          <IconButton
            size="small"
            onClick={() => setShowReloadConfirmDialog(true)}
            disabled={isReloadingDefaults}
            sx={{
              alignSelf: 'flex-start',
              mt: 0.75,
            }}
          >
            {isReloadingDefaults ? (
              <CircularProgress size={20} />
            ) : (
              <RefreshCwIcon size={20} />
            )}
          </IconButton>
        </Box>

        <List sx={{ mt: 2 }}>
          {sortedPlaces.map((place, placeIndex) => {
            return (
              <ListItem
                key={place.id}
                divider={placeIndex < sortedPlaces.length - 1}
                sx={{
                  px: 2,
                  py: 1.5,
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleDeletePlace(place.id)}
                    disabled={
                      deletingPlaceId === place.id || isAnyOperationInProgress
                    }
                  >
                    {deletingPlaceId === place.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Trash2Icon size={20} />
                    )}
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {getPlaceLabel(place)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            disabled={isAnyOperationInProgress}
            onClick={() => {
              const placeIds = marketPlaces.map(place => place.id);
              setInitialPlaces(placeIds);
              setSelectedPlaces(placeIds);
              setIsPlacePickerOpen(true);
            }}
          >
            {t('Add place')}
          </Button>
        </Box>
      </Box>
      <SlideDialog
        open={isPlacePickerOpen}
        onClose={() => {
          setIsPlacePickerOpen(false);
          setSelectedPlaces([]);
          setInitialPlaces([]);
        }}
        loading={getPlacesLoading || addPlacesLoading}
        title={t('Add Market Comparison Places')}
        onSave={handleAddPlaces}
        saveText={t('Add')}
        saveIcon={<PlusIcon />}
        cancelText={t('Cancel')}
        maxWidth="xl"
        fullWidth
        dialogProps={{
          slotProps: {
            paper: {
              sx: { height: '100%' },
            },
          },
        }}
      >
        <PlacePicker
          defaultValue={initialPlaces}
          onChange={setSelectedPlaces}
        />
      </SlideDialog>
      <SlideDialog
        open={showReloadConfirmDialog}
        onClose={() => setShowReloadConfirmDialog(false)}
        title={t('Reload Default Places')}
        onSave={handleReloadDefaults}
        loading={isReloadingDefaults}
        saveText={t('Reload')}
        saveIcon={<RefreshCwIcon />}
        cancelText={t('Cancel')}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          {t(
            'This will erase your current selection of market comparison places and reset to the default values. Are you sure you want to continue?',
          )}
        </Box>
      </SlideDialog>
      <FooterActionsComponentWithoutForm {...props} updating={isLoading} />
    </>
  );
};

export default StepMarketData;
